import { useState } from 'react';
import SectionHeading from './SectionHeading';
import { essencePosts } from '@/mocks/gossipData';

const filters = ['全部', '复盘向', '盘点向', '访谈向'];

const extraPoints: Record<string, string[]> = {
  '深度盘点': [
    '从一条节目片段出圈，到登上热搜、再到官方澄清，完整还原了 8 小时内的关键节点与讨论走向。',
    '梳理了讨论热度在早中晚三个时段的迁移路径，以及主流观点如何随新信息而改变。',
    '附上时间线与信息来源方向，方便你按图索骥回到原帖补看细节。',
  ],
  '话题盘点': [
    '汇总首播当晚讨论区里最高频的五个观点方向，涵盖环节设置、嘉宾表现与后期亮点。',
    '对比了节目播出前后的热搜词变化，直观呈现"首播即爆"是如何一步步形成的。',
    '整理了几类最有代表性的观众声音，帮你快速get大家到底在聊什么。',
  ],
  '幕后手记': [
    '对话多位幕后从业者，聊名场面诞生背后的排练、走位与临场调整。',
    '还原一个"看起来毫不费力"的镜头从构思到成片的完整流程。',
    '关于创作取舍、时间成本与团队协作的坦白分享，读起来很有干货。',
  ],
};

export default function Essence() {
  const [active, setActive] = useState('全部');
  const [openIdx, setOpenIdx] = useState<number | null>(null);

  const visible =
    active === '全部'
      ? essencePosts
      : essencePosts.filter((p) => {
          const key = active === '复盘向' ? '复盘' : active === '盘点向' ? '盘点' : '访谈';
          return p.meta.includes(key);
        });

  const openPost = openIdx !== null ? essencePosts[openIdx] : null;

  return (
    <section id="essence" className="scroll-mt-20 bg-background-100">
      <div className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
        <div className="flex flex-col justify-between gap-5 lg:flex-row lg:items-end">
          <SectionHeading
            kicker="精华盘点"
            title="昨日瓜田话题盘点"
            accent="必读精华"
            desc="编辑部从昨日海量讨论里挑出的高分盘点，把一件事的前因后果与各方观点讲透。"
          />
          <div className="inline-flex w-fit items-center gap-1 rounded-full bg-background-50 p-1 lg:mb-1">
            {filters.map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setActive(f)}
                className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                  active === f ? 'bg-primary-500 text-background-50' : 'text-foreground-600 hover:text-foreground-950'
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
          {visible.map((post, i) => (
            <article
              key={i}
              className="group flex cursor-pointer flex-col overflow-hidden rounded-2xl border border-background-200 bg-background-50 transition-all hover:-translate-y-1 hover:border-primary-200"
              onClick={() => setOpenIdx(essencePosts.findIndex((p) => p.title === post.title))}
            >
              <div className="relative h-52 w-full overflow-hidden md:h-56">
                <img
                  src={post.img}
                  alt={post.title}
                  title={`${post.title} - 昨日吃瓜盘点`}
                  className="h-full w-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                />
                <span className="absolute left-3 top-3 rounded-full bg-foreground-950/70 px-3 py-1 text-xs font-semibold text-background-100 backdrop-blur">
                  {post.tag}
                </span>
              </div>
              <div className="flex flex-1 flex-col p-5">
                <h3 className="font-heading text-lg font-bold leading-snug text-foreground-950 group-hover:text-primary-700">
                  {post.title}
                </h3>
                <p className="mt-2 flex-1 text-sm leading-6 text-foreground-600">{post.digest}</p>
                <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-3 text-xs text-foreground-500">
                  <span className="inline-flex items-center gap-1.5">
                    <i className="ri-time-line" /> {post.meta}
                  </span>
                  <span className="inline-flex items-center gap-1 font-semibold text-primary-600">
                    点开看全文 <i className="ri-arrow-right-line transition-transform group-hover:translate-x-0.5" />
                  </span>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>

      {/* Detail modal */}
      {openPost ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-foreground-950/50 p-4 backdrop-blur-sm"
          onClick={() => setOpenIdx(null)}
        >
          <div
            className="max-h-[88vh] w-full max-w-2xl overflow-auto rounded-2xl bg-background-50"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative h-52 w-full overflow-hidden md:h-60">
              <img src={openPost.img} alt={openPost.title} className="h-full w-full object-cover object-center" />
              <span className="absolute left-4 top-4 rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold text-background-50">
                {openPost.tag}
              </span>
              <button
                type="button"
                onClick={() => setOpenIdx(null)}
                aria-label="关闭"
                className="absolute right-4 top-4 flex h-9 w-9 cursor-pointer items-center justify-center rounded-full bg-background-50/90 text-foreground-800 hover:bg-background-50"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="p-5 md:p-7">
              <span className="text-xs font-medium text-foreground-500">{openPost.meta}</span>
              <h3 className="mt-2 font-heading text-xl font-bold leading-snug text-foreground-950 md:text-2xl">
                {openPost.title}
              </h3>
              <p className="mt-3 text-sm leading-7 text-foreground-700">{openPost.digest}</p>
              <div className="mt-5 rounded-2xl bg-secondary-50 p-4 md:p-5">
                <h4 className="font-heading text-sm font-bold text-foreground-950">这篇看点在哪</h4>
                <ul className="mt-3 flex flex-col gap-2.5">
                  {(extraPoints[openPost.tag] ?? []).map((pt, pi) => (
                    <li key={pi} className="flex gap-2 text-sm leading-6 text-foreground-700">
                      <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent-500" />
                      {pt}
                    </li>
                  ))}
                </ul>
              </div>
              <a
                href="#subscribe"
                onClick={() => setOpenIdx(null)}
                className="mt-5 inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                订阅昨日瓜报，每天准时送达 <i className="ri-mail-send-line" />
              </a>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}